import { z } from 'zod';
import { Instant, LocalDate, TenantSlug, Uuid } from './common.js';
import { PublicEmployee, PublicService } from './catalog.js';
import { CancellationActor } from './booking.js';
import { PublicApiAppointment, PublicApiClient } from './public-api.js';
// Вебхуки публичного API (ADR-036 §5).
//
// Вебхуки — не удобство, а способ не быть уничтоженными опросом: без них интегратор
// заменяет их циклом GET, и мы получаем постоянную нагрузку, а он — задержку в минуты.
//
// Тела событий переиспользуют представления из ./booking.ts и ./catalog.ts, а не
// описывают свои. Иначе полезная нагрузка вебхука разъедется с ответом API в первый же
// месяц, и потребитель, сверяющий событие с GET, увидит два разных снимка одной записи.
// ───────────────────────── перечень событий ─────────────────────────
/**
 * Формат «<ресурс>.<что произошло, в прошедшем времени>».
 *
 * Новое значение enum — НЕ ломающее изменение (ADR-036 §3.1): потребитель обязан
 * иметь ветку «неизвестный тип» и не падать. Взамен мы анонсируем новые типы
 * за 30 дней и сначала выпускаем их в песочницу.
 */
export const WebhookEventType = z.enum([
    'appointment.created',
    'appointment.confirmed',
    'appointment.rescheduled',
    'appointment.cancelled',
    'appointment.completed',
    'appointment.no_show',
    'client.created',
    'client.updated',
    'client.merged',
    // Отдаётся наружу ОБЯЗАТЕЛЬНО: интегратор-рассыльщик, не узнавший об отзыве согласия,
    // делает нарушителем салон перед ФАС и РКН. Не дать технической возможности узнать —
    // значит спроектировать нарушение.
    'client.consent_revoked',
    // Агрегированное, а не «на каждую строку занятости»: одно нажатие «поставить смены
    // на месяц» порождает сотни строк, и сотни вебхуков на одно действие человека
    // выжгли бы и наш воркер, и чужой endpoint.
    'schedule.changed',
    'service.changed',
    'employee.changed',
    // Служебное: им же проверяется владение endpoint'ом перед подпиской.
    'webhook.test',
]);
/**
 * Какой скоуп нужен подписке на событие. Подписка без скоупа — это способ получать
 * ПД в обход модели доступа: поток вебхуков ничем не отличается от выгрузки,
 * кроме направления соединения.
 */
export const EVENT_REQUIRED_SCOPE = {
    'appointment.created': 'appointments.read',
    'appointment.confirmed': 'appointments.read',
    'appointment.rescheduled': 'appointments.read',
    'appointment.cancelled': 'appointments.read',
    'appointment.completed': 'appointments.read',
    'appointment.no_show': 'appointments.read',
    'client.created': 'clients.read',
    'client.updated': 'clients.read',
    'client.merged': 'clients.read',
    'client.consent_revoked': 'clients.read',
    'schedule.changed': 'schedule.read',
    'service.changed': 'catalog.read',
    'employee.changed': 'catalog.read',
    'webhook.test': null,
};
// ───────────────────────── тела событий ─────────────────────────
/**
 * Событие несёт разумный снимок ресурса, чтобы не заставлять делать GET на каждое
 * событие и не съедать лимиты. Но снимок — не источник истины: при любом сомнении
 * истина в GET /v1/public-api/appointments/{id} (ADR-036 §5.5 п.3).
 */
/*
 * Запись в теле события — PublicApiAppointment, ТА ЖЕ форма, что у GET /appointments/{id}:
 * её собирает одна SQL-функция planerius_public_appointment (db/operational/17), и снимок
 * в событии не может разойтись с ответом API. Не AppointmentView витрины: в нём ссылки
 * «отменить/перенести» — предъявительские токены, которым в чужих логах не место.
 */
export const AppointmentEventData = z.object({
    appointment: PublicApiAppointment,
});
/** Перенос: и старое, и новое время в одном теле — иначе потребитель не поймёт, что менять. */
export const AppointmentRescheduledData = z.object({
    appointment: PublicApiAppointment,
    previousStartAt: Instant,
    previousEndAt: Instant,
    /** null, если мастер не менялся. */
    previousEmployeeId: Uuid.nullable(),
    initiatedBy: CancellationActor,
});
/**
 * Отмена. cancelledBy переиспользует CancellationActor из ./booking.ts: там значение
 * называется staff, а не salon, и заводить второе имя для того же факта нельзя —
 * два словаря одного перечисления гарантированно разъедутся при первом же изменении.
 *
 * Код причины отдаётся, свободный текст причины — нет: в комментарии администратора
 * лежит что угодно, включая сведения о человеке, выходящие за рамки поручения.
 */
export const AppointmentCancelledData = z.object({
    appointment: PublicApiAppointment,
    cancelledBy: CancellationActor,
    reasonCode: z.string().nullable(),
});
/** Карточка в теле маскирована ровно так же, как в ответе API: маска зависит от clients.pii. */
export const ClientEventData = z.object({
    client: PublicApiClient,
});
/**
 * Слияние карточек. Оба идентификатора обязательны: без mergedFromId у потребителя
 * остаётся висячая ссылка на карточку, которой больше нет, и он тихо теряет историю.
 */
export const ClientMergedData = z.object({
    survivorId: Uuid,
    mergedFromId: Uuid,
    mergedAt: Instant,
});
/**
 * Вид согласия. personal_data отозвать нельзя молча: это остановка обработки целиком.
 * channel_* — отписка от канала (SMS, Telegram, MAX, push, почта): рассыльщику она важна
 * не меньше рекламной — сообщение в канал, от которого человек отписался, та же рассылка
 * без согласия.
 */
export const ConsentKind = z.enum([
    'personal_data',
    'ads',
    'photo',
    'channel_sms',
    'channel_telegram',
    'channel_max',
    'channel_push',
    'channel_email',
]);
export const ClientConsentRevokedData = z.object({
    clientId: Uuid,
    consentKind: ConsentKind,
    /** Версия текста, на который человек когда-то согласился, — нужна для доказательства. */
    version: z.string(),
    revokedAt: Instant,
});
/**
 * Изменение расписания — агрегат по сотруднику и диапазону дат.
 * Детали потребитель забирает запросом: перечислять здесь каждый рабочий день
 * значило бы слать сотни событий на одно нажатие кнопки в кабинете.
 */
export const ScheduleChangedData = z.object({
    employeeId: Uuid,
    branchId: Uuid.nullable(),
    dateFrom: LocalDate,
    dateTo: LocalDate,
});
/** Что именно произошло с сущностью каталога. Удаления нет: услуги и мастера отключаются. */
export const CatalogChangeKind = z.enum(['created', 'updated', 'disabled']);
export const ServiceChangedData = z.object({
    changeKind: CatalogChangeKind,
    service: PublicService,
});
export const EmployeeChangedData = z.object({
    changeKind: CatalogChangeKind,
    employee: PublicEmployee,
});
/** Служебное тело проверки endpoint'а: ПД в нём нет и быть не должно. */
export const WebhookTestData = z.object({
    endpointId: Uuid,
    message: z.string(),
});
// ───────────────────────── конверт события ─────────────────────────
/** Версия схемы ПОЛЕЗНОЙ НАГРУЗКИ, отдельно от мажора API: тела эволюционируют своим темпом. */
export const WebhookSpecVersion = z.enum(['v1']);
/**
 * Общие поля конверта.
 *
 * tenant — slug, а не UUID: наружу внутренние идентификаторы платформы не уходят (ADR-036 §9).
 * sequence — монотонно растущее целое В ПРЕДЕЛАХ ТЕНАНТА, проставляется в момент
 * фиксации факта в той же транзакции, что и сам факт. Порядок доставки не гарантируется,
 * и sequence — то, чем потребитель отличает свежее событие от отставшего:
 * событие с sequence меньшим, чем уже применённый для этого ресурса, игнорируется.
 * occurredAt — момент ФАКТА, а не отправки.
 */
const webhookEnvelopeBase = {
    id: Uuid,
    specVersion: WebhookSpecVersion,
    occurredAt: Instant,
    sequence: z.number().int().nonnegative(),
    tenant: TenantSlug,
};
/**
 * Событие целиком. Разбор через discriminatedUnion по type: потребитель получает
 * узкий тип тела, а не «data: unknown», и не пишет свои приведения типов.
 */
export const WebhookEvent = z.discriminatedUnion('type', [
    z.object({ ...webhookEnvelopeBase, type: z.literal('appointment.created'), data: AppointmentEventData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('appointment.confirmed'), data: AppointmentEventData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('appointment.rescheduled'), data: AppointmentRescheduledData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('appointment.cancelled'), data: AppointmentCancelledData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('appointment.completed'), data: AppointmentEventData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('appointment.no_show'), data: AppointmentEventData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('client.created'), data: ClientEventData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('client.updated'), data: ClientEventData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('client.merged'), data: ClientMergedData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('client.consent_revoked'), data: ClientConsentRevokedData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('schedule.changed'), data: ScheduleChangedData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('service.changed'), data: ServiceChangedData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('employee.changed'), data: EmployeeChangedData }),
    z.object({ ...webhookEnvelopeBase, type: z.literal('webhook.test'), data: WebhookTestData }),
]);
// ───────────────────────── заголовки и подпись доставки ─────────────────────────
/**
 * Заголовки исходящего POST. Дублируют поля конверта намеренно: потребитель должен
 * уметь отсеять дубль и опознать тип события, не разбирая тело, — а тело до проверки
 * подписи разбирать нельзя вообще.
 */
export const WEBHOOK_HEADERS = {
    eventId: 'Planerius-Event-Id',
    eventType: 'Planerius-Event-Type',
    signature: 'Planerius-Signature',
    /** Идентификатор попытки доставки — то, что подставляется в .../deliveries/{id}/retry. */
    deliveryId: 'Planerius-Delivery-Id',
    /** Номер попытки, начиная с 1: помогает потребителю отличить ретрай от нового события. */
    attempt: 'Planerius-Attempt',
};
/** Версия схемы подписи. Вторая версия появится только вместе с новым алгоритмом. */
export const WEBHOOK_SIGNATURE_VERSION = 'v1';
/**
 * Окно допустимого расхождения часов, секунды.
 * Метка времени t и есть защита от повтора: без её проверки перехваченный когда-то
 * запрос можно воспроизводить вечно, и подпись будет валидной.
 */
export const WEBHOOK_TIMESTAMP_TOLERANCE_SECONDS = 300;
/**
 * Значение Planerius-Signature: «t=<unix>,v1=<hex>».
 * Во время ротации секрета 72 часа отдаются ДВЕ подписи v1 в одном заголовке —
 * потребитель обязан считать доставку подлинной, если совпала хотя бы одна.
 */
export const WebhookSignatureHeader = z
    .string()
    .regex(/^t=\d{10,},v1=[0-9a-f]{64}(?:,v1=[0-9a-f]{64})?$/, 'ожидается t=<unix>,v1=<hex> (при ротации — две подписи v1)');
/**
 * Подписываемая строка: «<t>.<СЫРОЕ тело>», HMAC-SHA-256 секретом endpoint'а, hex.
 * Сырое тело — это байты до разбора JSON: любая нормализация (переупорядочивание
 * ключей, смена отступов, перекодировка) ломает подпись, и потребитель получит
 * необъяснимый signature mismatch на половине доставок.
 */
export function webhookSigningPayload(timestampSeconds, rawBody) {
    return `${timestampSeconds}.${rawBody}`;
}
/** Разобранный заголовок подписи. Сравнивать подписи потребитель обязан за постоянное время. */
export const ParsedWebhookSignature = z.object({
    timestampSeconds: z.number().int().positive(),
    signatures: z.array(z.string().regex(/^[0-9a-f]{64}$/)).min(1).max(2),
});
export function parseWebhookSignature(header) {
    const parts = header.split(',');
    const timestampPart = parts.find((p) => p.startsWith('t='));
    if (timestampPart === undefined)
        return null;
    const timestampSeconds = Number(timestampPart.slice(2));
    if (!Number.isInteger(timestampSeconds) || timestampSeconds <= 0)
        return null;
    const signatures = parts.filter((p) => p.startsWith('v1=')).map((p) => p.slice(3));
    if (signatures.length === 0)
        return null;
    const parsed = ParsedWebhookSignature.safeParse({ timestampSeconds, signatures });
    return parsed.success ? parsed.data : null;
}
// ───────────────────────── правила доставки (контракт с потребителем) ─────────────────────────
/**
 * Поведение доставки, вынесенное в константы: те же числа печатаются в документации,
 * и второго их описания быть не должно.
 */
export const WEBHOOK_DELIVERY_RULES = {
    /** Только https и только 443: любой другой порт — либо опечатка, либо попытка SSRF. */
    scheme: 'https',
    port: 443,
    /**
     * Пять секунд. Правило потребителя, написанное в документации крупно:
     * ответить 2xx сразу, обработать асинхронно. Синхронная обработка в чужом коде
     * делает наш воркер заложником чужой базы.
     */
    timeoutSeconds: 5,
    /** Больше не читаем: тело ответа нам нужно только для диагностики. */
    maxResponseBytes: 16 * 1024,
    /** Редиректы не выполняем: 307 на другой хост — классический способ увести запрос. */
    followRedirects: false,
    /**
     * Сразу → 1 мин → 5 мин → 30 мин → 2 ч → 6 ч → 24 ч, джиттер ±20 %.
     * Горизонт выбран так, чтобы суточный простой чужого сервера (ночной деплой,
     * истёкший сертификат, переезд) переживался без ручного вмешательства;
     * больше суток — это уже поломка, и её надо чинить, а не ретраить.
     */
    retryDelaysSeconds: [0, 60, 300, 1800, 7200, 21600, 86400],
    jitterRatio: 0.2,
    /** 24 часа подряд без единой успешной доставки → endpoint отключается автоматически. */
    autoDisableAfterHours: 24,
    /** Мёртвая очередь живёт 7 суток: дольше — это уже не «починю и переотправлю». */
    deadLetterRetentionDays: 7,
    /** Дедупликация — по id события. Доставка at-least-once по построению. */
    deduplicationWindowHours: 24,
    /** Ручная переотправка лимитируется отдельно, иначе replay сам станет нагрузкой. */
    manualReplayPerHour: 1000,
};
// ───────────────────────── управление endpoint'ами ─────────────────────────
/**
 * Адрес доставки. Схема отсекает то, что видно по строке: не-https, нестандартный порт,
 * литеральный IP и .local. Полноценный SSRF-фильтр — на сервере и после резолва имени
 * (ADR-036 §5.6): проверить адрес по строке недостаточно, потому что имя может
 * резолвиться во внутренний адрес уже в момент соединения.
 */
/**
 * Форма допустимого адреса, проверяемая по строке: https, доменное имя,
 * порт только 443 либо опущен, без localhost, без литерального IP и без [IPv6].
 * Регулярное выражение, а не разбор URL, потому что пакет контрактов собирается
 * и для браузера, и для сервера: тянуть в него глобалы среды не следует.
 */
const WEBHOOK_URL_PATTERN = /^https:\/\/(?!localhost(?::|\/|$))(?!\d{1,3}(?:\.\d{1,3}){3}(?::|\/|$))(?!\[)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(?::443)?(?:\/[^\s]*)?$/;
/** Зона .local вырезана отдельно: это mDNS внутри периметра салона, а не адрес в интернете. */
const WEBHOOK_URL_LOCAL_PATTERN = /^https:\/\/[^/]*\.local(?::|\/|$)/;
export const WebhookUrl = z
    .url()
    .refine((value) => WEBHOOK_URL_PATTERN.test(value), {
    error: 'ожидается https-адрес по доменному имени на порту 443',
})
    .refine((value) => !WEBHOOK_URL_LOCAL_PATTERN.test(value), {
    error: 'адреса в зоне .local недопустимы',
});
/**
 * pending_verification — endpoint создан, но поток событий на него не идёт, пока
 * не пришёл 2xx на webhook.test. Подписать чужой сервер на поток чужих ПД нельзя:
 * проверка владения обязательна, а не «желательна» (ADR-036 §5.6).
 */
/**
 * Значения повторяют перечисление БД `webhook_endpoint_status` один в один.
 * `paused` — выключен салоном вручную (обратимо, без повторной проверки владения),
 * `disabled` — выключен нами: сутки отказов либо 410 Gone.
 */
export const WebhookEndpointStatus = z.enum(['pending_verification', 'active', 'paused', 'disabled']);
/** Почему endpoint отключён: салоном, автоматикой после суток отказов или самим потребителем. */
export const WebhookDisableReason = z.enum(['manual', 'auto_failures', 'gone']);
export const CreateWebhookEndpointRequest = z.object({
    url: WebhookUrl,
    description: z.string().trim().max(500).optional(),
    /**
     * Подписка на конкретные типы, а не «на всё». Пустой список запрещён:
     * подписка на всё — это способ получать ПД, которые интеграции не нужны,
     * и она же гарантирует поток событий, который потребитель не обрабатывает.
     */
    eventTypes: z.array(WebhookEventType).min(1),
    /** Ограничение потока конкретными филиалами; пусто = филиалы гранта. */
    branchIds: z.array(Uuid).default([]),
});
export const UpdateWebhookEndpointRequest = z.object({
    description: z.string().trim().max(500).nullable().optional(),
    eventTypes: z.array(WebhookEventType).min(1).optional(),
    branchIds: z.array(Uuid).optional(),
    /** Включение обратно после auto_failures требует повторной проверки endpoint'а. */
    enabled: z.boolean().optional(),
});
export const WebhookEndpointView = z.object({
    id: Uuid,
    url: z.url(),
    description: z.string().nullable(),
    eventTypes: z.array(WebhookEventType),
    branchIds: z.array(Uuid),
    status: WebhookEndpointStatus,
    /** Последние 4 символа секрета: узнать endpoint в своей конфигурации, не раскрывая секрет. */
    secretLast4: z.string().length(4),
    /** Непусто, пока идёт 72-часовое перекрытие ротации: в заголовке две подписи. */
    previousSecretExpiresAt: Instant.nullable(),
    createdAt: Instant,
    verifiedAt: Instant.nullable(),
    disabledAt: Instant.nullable(),
    disableReason: WebhookDisableReason.nullable(),
    /** Здоровье потока: по этим трём полям салон видит сломанную интеграцию сам. */
    lastSuccessAt: Instant.nullable(),
    lastFailureAt: Instant.nullable(),
    consecutiveFailures: z.number().int().nonnegative(),
    /** Почему последняя проверка не прошла — нашими словами («ваш сервер ответил 502»). */
    verificationFailedReason: z.string().nullable(),
    /** Сколько доставок лежит в мёртвой очереди за 7 суток — для «Переотправить неудачные». */
    deadDeliveries7d: z.number().int().nonnegative(),
});
/**
 * Ответ на создание и на ротацию секрета — единственное место, где секрет подписи
 * существует. Как и ключ API, он не хранится в открытом виде и не показывается повторно.
 */
export const WebhookEndpointCreated = z.object({
    endpoint: WebhookEndpointView,
    /**
     * Секрет подписи: «whsec_» и 32 случайных байта в base64url. Им и считается HMAC
     * доставки. Префикс — чтобы секрет узнавался в логах и сканерах утечек, как ключ plnr_.
     */
    secret: z.string().regex(/^whsec_[A-Za-z0-9_-]{43}$/),
    secretShownOnce: z.literal(true),
    /** Проверка адреса сразу при создании (webhook.test); результат — ниже по схеме. */
    test: z.lazy(() => TestWebhookEndpointResponse),
});
/**
 * Эхо-запрос: мы шлём на endpoint событие webhook.test и возвращаем ровно то,
 * что увидели. Это и проверка владения адресом, и единственный способ для интегратора
 * отладить проверку подписи, не дожидаясь настоящей записи живого клиента.
 */
export const TestWebhookEndpointRequest = z.object({
    message: z.string().trim().max(200).default('ping'),
});
export const TestWebhookEndpointResponse = z.object({
    eventId: Uuid,
    deliveryId: Uuid,
    ok: z.boolean(),
    /** null, когда соединение не состоялось вовсе: тогда смотреть надо в error. */
    responseStatus: z.number().int().nullable(),
    elapsedMs: z.number().int().nonnegative(),
    /**
     * Причина отказа нашими словами: таймаут, отказ SSRF-фильтра, ошибка TLS.
     * Тексты и коды системных ошибок наружу не идут — только наши формулировки.
     */
    error: z.string().nullable(),
    /** Первый килобайт ответа: интегратор видит, что вернул его собственный обработчик. */
    responseBodyPreview: z.string().nullable(),
});
export const WebhookEndpointList = z.object({
    items: z.array(WebhookEndpointView),
    nextCursor: z.string().nullable(),
});
// ───────────────────────── доставки и переотправка ─────────────────────────
/**
 * Состояние попытки доставки. Значения повторяют перечисление БД
 * `webhook_delivery_status` один в один: раньше контракт объявлял `delivering`
 * и `delivered`, которых в базе нет, и не имел `dead` и `dropped`, которые она
 * проставляет, — то есть фильтр `?status=` из документации не нашёл бы ничего,
 * а строка мёртвой очереди не прошла бы собственную схему ответа.
 *
 * `dead` — ретраи исчерпаны, строка живёт 7 суток и доступна для переотправки;
 * `dropped` — потребитель ответил 410 Gone, ретраев нет.
 */
export const WebhookDeliveryStatus = z.enum([
    'pending',
    'in_flight',
    'succeeded',
    'failed',
    'dead',
    'dropped',
]);
export const ListWebhookDeliveriesQuery = z.object({
    endpointId: Uuid.optional(),
    status: WebhookDeliveryStatus.optional(),
    eventType: WebhookEventType.optional(),
    since: Instant.optional(),
    until: Instant.optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).default(20),
});
/**
 * Попытка доставки в мёртвой очереди. Хранится 7 суток вместе с телом запроса
 * и первым килобайтом ответа: без тела «переотправить после починки» превращается
 * в «расскажите, что там было».
 */
export const WebhookDeliveryView = z.object({
    /** Единственный внутренний идентификатор, который уходит наружу, — он нужен для retry. */
    deliveryId: Uuid,
    eventId: Uuid,
    eventType: WebhookEventType,
    endpointId: Uuid,
    sequence: z.number().int().nonnegative(),
    status: WebhookDeliveryStatus,
    attempt: z.number().int().positive(),
    nextAttemptAt: Instant.nullable(),
    responseStatus: z.number().int().nullable(),
    responseBodyPreview: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: Instant,
    deliveredAt: Instant.nullable(),
});
export const WebhookDeliveryList = z.object({
    items: z.array(WebhookDeliveryView),
    nextCursor: z.string().nullable(),
});
/**
 * Переотправка диапазона — то, что нужно после починки на чужой стороне:
 * «доставьте всё, что мы пропустили с 14:00 до 19:00». Диапазон задаётся по sequence,
 * а не по времени: время на стороне потребителя и у нас может разойтись, sequence — нет.
 */
export const ReplayWebhookEventsRequest = z
    .object({
    endpointId: Uuid,
    fromSequence: z.number().int().nonnegative(),
    toSequence: z.number().int().nonnegative(),
    eventTypes: z.array(WebhookEventType).default([]),
})
    .refine((r) => r.fromSequence <= r.toSequence, {
    error: 'fromSequence не может быть больше toSequence',
    path: ['toSequence'],
});
export const ReplayWebhookEventsResponse = z.object({
    /** Сколько событий поставлено в очередь. Лимит — 1 000 в час на тенант. */
    queued: z.number().int().nonnegative(),
    /** Сколько отброшено, потому что вышли за ретеншн outbox (30 суток). */
    skippedExpired: z.number().int().nonnegative(),
});
//# sourceMappingURL=public-api-webhooks.js.map